import { useContext } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { UserContext } from '../..';
import { ROLE_ADMIN } from '../../constants';
import { RowResolvers } from './DocumentsTable';
import { fullName, useFilterOptions } from './useFilterOptions';

// How a document row becomes what the table shows:
// archiver name, service name, dates and whether the user can
// edit or delete. Shared by the Home and Résultats, which paint the same
// table over two different listings.

dayjs.extend(utc);

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The backend stores `issuing_date` as UTC midnight of the chosen day and
// filters both dates by UTC day; they are formatted in UTC so that the day
// shown is the one entered and the one the filter finds, whatever
// the browser's time zone is.
export const formatDate = (value: string): string => {
  const date = dayjs.utc(value);
  return date.isValid() ? date.format('DD/MM/YYYY') : '—';
};

export const useDocumentResolvers = () => {
  const userContext = useContext(UserContext);
  const me = userContext?.mainUser ?? null;
  const myId = me?.id ?? '';
  const isAdmin = me?.role === ROLE_ADMIN;

  const { users, services } = useFilterOptions(isAdmin);

  const userName = (id?: string): string => {
    if (!id) return '—';
    if (id === myId) return fullName(me) || 'Moi';

    const found = users.find((user) => user.id === id);
    if (found) return fullName(found) || id;

    // An id that cannot be resolved (a USER does not have the users list)
    // is not shown raw; free text, yes.
    return UUID_PATTERN.test(id) ? '—' : id;
  };

  const serviceName = (id?: string): string =>
    services.find((service) => service.id === id)?.name || id || '—';

  const resolvers: RowResolvers = {
    archiverName: (document) => userName(document.archiver),
    serviceName: (document) => serviceName(document.service),
    formatDate,
    // The same as `documentAccess.canWrite` in the backend. Without `archiver`
    // in the response there is no way to know, and the action is not offered.
    canWrite: (document) =>
      isAdmin || (Boolean(document.archiver) && document.archiver === myId),
  };

  return { me, myId, isAdmin, users, services, userName, serviceName, resolvers };
};
